import { requireDraftMode } from "@components/branch/help";
import { enterDraftMode, useDraftVersionId, useIsInDraftMode } from "@stores/data.store";
import { PropsWithChildren, useCallback, useState, cloneElement, isValidElement, useMemo } from 'react'
import styled from 'styled-components';
import { Modal } from 'react-bootstrap';
import { useAuthState, PlbAuthState } from "@services/auth/auth.service";
import { Role } from "../../constants/roles";

const Blocker = styled.div`
position: absolute;
top: 0px;
left: 0px;
width: 100%;
height: 100%;
z-index: 5000;

background: rgba(0, 0, 0, 0.5);

.actions {
    text-align: center;
    color: white;
    font-family: "Poppins";
    font-size: 18px;
    font-weight: 500;
    width: 300px;
    margin: 100px auto;
}
`

const Styles = styled.div`
position: relative;
height: 100%;
`

export const isReadOnlyUser = (authState: PlbAuthState | null): boolean => {
    if (!authState?.roles || authState.roles.length === 0) {
        return false;
    }
    
    const roleStrings = authState.roles.map(role => {
        if (typeof role === 'string') {
            return role;
        }
        return (role as any)?.key || (role as any)?.name || '';
    });
    
    const hasBuilderOrAdmin = roleStrings.some(role => {
        const normalizedRole = role?.toLowerCase?.() || role;
        return normalizedRole === Role.Builder.toLowerCase() || 
               normalizedRole === Role.Admin.toLowerCase();
    });
    
    const hasReadOnly = roleStrings.some(role => {
        const normalizedRole = role?.toLowerCase?.() || role;
        return normalizedRole === Role.ReadOnly.toLowerCase() || normalizedRole === 'read only';
    });
    
    return hasReadOnly && !hasBuilderOrAdmin;
};

export const useIsReadOnlyUser = (): boolean => {
    const authState = useAuthState();
    return useMemo(() => isReadOnlyUser(authState), [authState]);
};

type DraftModeRequiredProps = {
    showDraftModeLink?: boolean;
    justHideChildren?: boolean;
}

export function DraftOnly({ children }: PropsWithChildren) {
    const inDraftMode = useIsInDraftMode();
    if (inDraftMode) {
        return <>{children}</>
    }
    return <></>;
}

interface ReadOnlyProps {
    forceEnable?: boolean;
}
export function ReadOnly({ children, forceEnable }: PropsWithChildren<ReadOnlyProps>) {
    const inDraftMode = useIsInDraftMode();
    if (inDraftMode || forceEnable) {
        return <>{children}</>
    }
    return <span className="disabled">{children}</span>;
}




export function DraftModeRequired({showDraftModeLink = false, children, justHideChildren}: PropsWithChildren<DraftModeRequiredProps>) {
    const {data: draftVersionId} = useDraftVersionId();

    const beginDraftClick = useCallback(async () => {
        await enterDraftMode('draft_mode_required');
    }, [draftVersionId]);

    if (!draftVersionId && justHideChildren) {
        return <></>
    } else if (justHideChildren) {
        return <>{children}</>
    }

    return <Styles>
        {!draftVersionId && <Blocker>
            <div className="actions">
                You need to be in draft mode to make changes.
                <button className="btn btn-pliable" onClick={beginDraftClick}>Enter draft mode <i className="mdi mdi-pencil"></i></button>
            </div>
        </Blocker>}
        {children}
    </Styles>
    // if (!!draftVersionId){
    //     return <>{children}</>
    // }

    // if (showDraftModeLink) {
    //     return <button className="btn" onClick={beginDraftClick}>Edit <i className="mdi mdi-pencil"></i></button>
    // }

    // return <></>
}

interface ProdModeRequiredProps {
    
}

export function ProdOnly({ children }: PropsWithChildren) {
    const inDraftMode = useIsInDraftMode();
    if (!inDraftMode) {
        return <>{children}</>
    }
    return <></>;
}

export const ProdModeRequired = (props: PropsWithChildren<ProdModeRequiredProps>) => {
    const {data: draftVersionId} = useDraftVersionId();
    const beginDraftClick = useCallback(async () => {
        await enterDraftMode('draft_mode_required');
    }, [draftVersionId]);

    if (!draftVersionId) {
        return <>
            {props.children}
        </>
    }
    return <></>
}


export function BlockInProd({ children }: PropsWithChildren) {
    const inDraftMode = useIsInDraftMode();
    if (inDraftMode) {
        return <>{children}</>
    }
    return (
        <div className="card-body">
            <div className="text-center align-items">
                <h3>You're currently in Production Mode...</h3>
                <p>Please enter Develop Mode to continue</p>
                <button className="btn btn-pliable" onClick={() => enterDraftMode('block_in_prod')}>Enter Develop Mode <i className="mdi mdi-pencil"></i></button>
            </div>
        </div>
    );
}

const ReadOnlyModal = ({ show, onHide }: { show: boolean; onHide: () => void }) => (
    <Modal show={show} onHide={onHide} centered>
        <Modal.Header closeButton>
            <Modal.Title>Action Restricted</Modal.Title>
        </Modal.Header>
        <Modal.Body>
            <div className="text-center mb-3">
                <i className="mdi mdi-lock" style={{ fontSize: '64px', color: '#FFC107' }}></i>
            </div>
            <h2 className="text-center mb-2 fw-bold">Read Only Access</h2>
            <p className="text-center mb-3">
                You are currently viewing with Read Only permissions and cannot perform this action.
            </p>
            <div className="alert alert-info d-flex align-items-center mb-2" style={{ backgroundColor: '#E3F2FD', border: 'none', borderRadius: '8px' }}>
                <i className="mdi mdi-information-outline me-2" style={{ fontSize: '20px' }}></i>
                <div style={{ color: '#000' }}>
                    This feature is available to <strong>Builders</strong> and <strong>Administrators</strong>.
                </div>
            </div>
            <p className="text-center mb-0" style={{ fontWeight: 300 }}>
                Need access? Please contact your Administrator for assistance.
            </p>
        </Modal.Body>
        <Modal.Footer>
            <button className="btn btn-light" onClick={onHide}>
                Close
            </button>
        </Modal.Footer>
    </Modal>
);

/**
 * BuilderRequired component wraps elements that require Builder or Admin role.
 * If a ReadOnly user tries to interact with the wrapped element, a modal warning is shown.
 */
export function BuilderRequired({ children }: PropsWithChildren) {
    const [showModal, setShowModal] = useState(false);
    const isReadOnly = useIsReadOnlyUser();

    const handleInteraction = useCallback((e: React.MouseEvent) => {
        if (isReadOnly) {
            e.preventDefault();
            e.stopPropagation();
            setShowModal(true);
        }
    }, [isReadOnly]);

    if (!isReadOnly) {
        return <>{children}</>;
    }

    if (isValidElement(children)) {
        const childProps = (children as React.ReactElement<any>).props || {};
        
        const wrappedChild = cloneElement(children as React.ReactElement<any>, {
            onClick: (e: React.MouseEvent) => {
                e.preventDefault();
                e.stopPropagation();
                handleInteraction(e);
            },
            style: {
                ...(childProps.style || {}),
                opacity: 0.6,
                cursor: 'pointer',
            },
            ...(childProps.href && !childProps.to ? { href: '#' } : {}),
        } as any);

        return (
            <>
                {wrappedChild}
                <ReadOnlyModal show={showModal} onHide={() => setShowModal(false)} />
            </>
        );
    }

    return (
        <>
            <div onClick={handleInteraction} style={{ display: 'block', width: '100%', opacity: 0.6 }}>
                {children}
            </div>
            <ReadOnlyModal show={showModal} onHide={() => setShowModal(false)} />
        </>
    );
}



